import { useEffect, useMemo, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useMsal } from "@azure/msal-react";
import { useTenant } from "../contexts/tenant-context";
import { API_BASE_URL, GLOBAL_ADMIN_TENANT_ID, SIGNALR_SCOPE } from "../constants/constants";
import { DocumentMoveJobResponse } from "../services/documentService";

interface UseDocumentMoveSignalROptions {
  onJobUpdated: (job: DocumentMoveJobResponse) => void;
  onConnectionError: (message: string) => void;
}

export const useDocumentMoveSignalR = ({
  onJobUpdated,
  onConnectionError,
}: UseDocumentMoveSignalROptions) => {
  const { instance } = useMsal();
  const { selectedTenantId } = useTenant();

  const connectionRef = useRef<HubConnection | null>(null);
  const onJobUpdatedRef = useRef(onJobUpdated);
  const onConnectionErrorRef = useRef(onConnectionError);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);

  onJobUpdatedRef.current = onJobUpdated;
  onConnectionErrorRef.current = onConnectionError;

  const shouldConnect = useMemo(() => {
    return Boolean(
      selectedTenantId
      && selectedTenantId !== GLOBAL_ADMIN_TENANT_ID
      && API_BASE_URL
      && SIGNALR_SCOPE
    );
  }, [selectedTenantId]);

  useEffect(() => {
    if (!shouldConnect) {
      setConnectionState(HubConnectionState.Disconnected);
      return;
    }

    let isCancelled = false;

    const connect = async () => {
      const activeAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!activeAccount) {
        onConnectionErrorRef.current(
          "Realtime updates are unavailable until authentication is ready."
        );
        return;
      }

      const baseUrl = API_BASE_URL.replace(/\/$/, "");
      const tenantQuery = encodeURIComponent(selectedTenantId!);
      const hubUrl = `${baseUrl}/hubs/folder-export-jobs?tenantId=${tenantQuery}`;

      const connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            const token = await instance.acquireTokenSilent({
              account: activeAccount,
              scopes: [SIGNALR_SCOPE],
            });

            return token.accessToken;
          },
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .configureLogging(LogLevel.Warning)
        .build();

      connectionRef.current = connection;

      connection.on("documentMoveJobUpdated", (job: DocumentMoveJobResponse) => {
        onJobUpdatedRef.current(job);
      });

      connection.onclose(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Disconnected);
        }
      });

      connection.onreconnecting(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Reconnecting);
        }
      });

      connection.onreconnected(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Connected);
        }
      });

      try {
        await connection.start();
        if (isCancelled) {
          await connection.stop();
          return;
        }

        setConnectionState(HubConnectionState.Connected);
      } catch (error) {
        if (!isCancelled) {
          onConnectionErrorRef.current(
            `Realtime connection failed: ${(error as Error).message}`
          );
          setConnectionState(HubConnectionState.Disconnected);
        }
      }
    };

    connect();

    return () => {
      isCancelled = true;
      const current = connectionRef.current;
      connectionRef.current = null;
      if (current) {
        current.stop().catch(() => undefined);
      }
    };
  }, [instance, selectedTenantId, shouldConnect]);

  return {
    isConnected: connectionState === HubConnectionState.Connected,
    connectionState,
  };
};
